import * as impersonationApi from "@/modules/access/impersonation/accessImpersonationApi.ts";
import { apiClientAtom } from "@/modules/api/apiClient.store.ts";
import { getResponseData, type ApiClient } from "@/modules/api/apiClient.ts";
import { isLoggedInAtom } from "@/modules/api/apikey.store.ts";
import { queryClientAtom } from "@/modules/api/queryClient.ts";
import { raiseRequestToast, type Toast } from "@/modules/notifications/toast.tsx";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { queryThen } from "@/utils/query/mergeQueries.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { mutationOptions } from "@/utils/query/runMutation.ts";
import type { QueryClient } from "@tanstack/query-core";
import { queryOptions, type QueryKey } from "@tanstack/react-query";
import type {
    User,
    UserMfaResponse,
    UserProfilePatchRequest,
    UserRoleResponse,
} from "@warrenio/api-spec/spec.oats.gen";
import { discardPromise } from "@warrenio/utils/promise/discardPromise";
import { MINUTES } from "@warrenio/utils/timeUnits";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { atom } from "jotai/vanilla";

//#region Queries
const queryKey: QueryKey = ["/user_resource/user"];

function userQuery(apiClient: ApiClient) {
    return queryOptions({
        queryKey,
        queryFn: async ({ signal }): Promise<User> => {
            return getResponseData(await apiClient.GET("/user-resource/user", { signal }));
        },
    });
}

export const userDataAtom = atomFromStandardQueryOptions(userQuery);

export const userDataPromiseAtom = atom(async (get) => {
    // TODO: Create a utility method to convert query to a promise
    if (get(isLoggedInAtom)) {
        const apiClient = get(apiClientAtom);
        return await get(queryClientAtom).fetchQuery(userQuery(apiClient));
    } else {
        return undefined;
    }
});
//#endregion

//#region Admin queries
function userRoleQuery(apiClient: ApiClient) {
    return queryOptions({
        queryKey: ["user_role"],
        queryFn: async ({ signal }): Promise<UserRoleResponse> => {
            return getResponseData(await apiClient.GET("/user-resource/user/role", { signal }));
        },
        staleTime: 60 * MINUTES,
    });
}

export const userRoleAtom = atomFromStandardQueryOptions(userRoleQuery);

export const userIsAdminAtom = atom((get) =>
    queryThen(get(userRoleAtom), ({ role }) => {
        switch (role) {
            case "admin":
            case "admin-accountant":
                return true;
            case "user":
                return false;
            default:
                exhaustiveSwitchCheck(role);
        }
    }),
);

export function isInternalEmail(email: string) {
    return email.endsWith("@warren.io") || email.endsWith("@oyenetwork.com");
}

//#endregion

const mfaQueryKey: QueryKey = ["mfa_is_active"];

export function userMfaQuery(apiClient: ApiClient) {
    return queryOptions({
        queryKey: mfaQueryKey,
        queryFn: async ({ signal }): Promise<UserMfaResponse> => {
            return getResponseData(await apiClient.GET("/user-resource/mfa/is_active", { signal }));
        },
    });
}

//#endregion

//#region Mutations
const toastOptions: Partial<Toast> = { icon: "jp-user-icon" };

export function changeProfileDataMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["/user-resource/user/profile"],
        async mutationFn({ body }: { body: UserProfilePatchRequest }) {
            return getResponseData(
                await apiClient.PATCH("/user-resource/user/profile", {
                    ...jsonEncodedBody,
                    body,
                }),
            );
        },
        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "User details updated",
                error: "Failed to update details",
            });
            await queryClient.invalidateQueries({ queryKey });
        },
    });
}

export function activateMfaMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["mfa_activate"],
        async mutationFn() {
            return getResponseData(await apiClient.POST("/user-resource/mfa/activate"));
        },
        onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "MFA activation started",
                error: "Failed to activate MFA",
                successType: "success",
            });
            // Do not wait for invalidation because we will be redirected to the MFA page anyway
            discardPromise(queryClient.invalidateQueries({ queryKey: mfaQueryKey }));
        },
    });
}

export function deactivateMfaMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["mfa_deactivate"],
        async mutationFn() {
            return getResponseData(await apiClient.POST("/user-resource/mfa/deactivate"));
        },
        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Mfa deactivated",
                error: "Failed to deactivate MFA",
            });
            await queryClient.invalidateQueries({ queryKey: mfaQueryKey });
        },
    });
}

export function acceptInvitationMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["accept_invitation"],
        async mutationFn({ uuid }: { uuid: string }) {
            return getResponseData(
                await apiClient.PUT("/user-resource/impersonation/{uuid}/accept", {
                    params: { path: { uuid } },
                }),
            );
        },
        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Access invitation accepted",
                error: "Failed to accept invitation",
                successType: "success",
            });
            await queryClient.invalidateQueries({ queryKey: impersonationApi.queryKey });
        },
    });
}

//#endregion Mutations
